import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import useMonthlySalaryStore from '@/store/hr/MonthlySalaryStore';
import { useAuth } from '@store/authStore';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthlySalaryReview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    period,
    employees,
    banner,
    loading,
    releasingId,
    error,
    fetchSummary,
    fetchBanner,
    release
  } = useMonthlySalaryStore();

  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    fetchSummary();
    fetchBanner();
  }, []);

  // Restore per-period banner dismissal.
  useEffect(() => {
    if (banner?.show && user?.id) {
      const key = `hrSalaryBanner:${banner.period.year}-${banner.period.month}:${user.id}`;
      setBannerDismissed(localStorage.getItem(key) === 'dismissed');
    }
  }, [banner, user?.id]);

  const dismissBanner = () => {
    if (banner && user?.id) {
      const key = `hrSalaryBanner:${banner.period.year}-${banner.period.month}:${user.id}`;
      localStorage.setItem(key, 'dismissed');
    }
    setBannerDismissed(true);
  };

  const periodLabel = useMemo(
    () => (period ? `${MONTH_NAMES[period.month - 1]} ${period.year}` : ''),
    [period]
  );

  const changeMonth = (delta: number) => {
    if (!period) return;
    let { year, month } = period;
    month += delta;
    if (month < 1) { month = 12; year -= 1; }
    if (month > 12) { month = 1; year += 1; }
    fetchSummary(year, month);
  };

  const releasedCount = employees.filter((e) => e.released).length;

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Monthly Attendance & Salary Release
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review each employee's punch-ins / punch-outs, then release salary for the month.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AccountBalanceWalletIcon />}
            onClick={() => navigate('/payroll')}
          >
            Open Payroll
          </Button>
        </Box>

        {banner?.show && !bannerDismissed && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={dismissBanner}>
            It's the first working day of the month — last month's attendance
            ({MONTH_NAMES[banner.period.month - 1]} {banner.period.year}) is ready for salary release.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, p: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          {/* Month selector + summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => changeMonth(-1)} disabled={loading} aria-label="previous month">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 160, textAlign: 'center' }}>
                {periodLabel}
              </Typography>
              <IconButton onClick={() => changeMonth(1)} disabled={loading} aria-label="next month">
                <ChevronRightIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {releasedCount}/{employees.length} released
            </Typography>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Punch-ins</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Punch-outs</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No employees found for this month.
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow key={emp.employee_id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{emp.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{emp.email}</Typography>
                      </TableCell>
                      <TableCell>{emp.role_name}</TableCell>
                      <TableCell align="center">{emp.total_punch_ins}</TableCell>
                      <TableCell align="center">{emp.total_punch_outs}</TableCell>
                      <TableCell align="center">
                        {emp.released ? (
                          <Tooltip
                            title={emp.released_at ? `Released ${emp.released_at}${emp.released_by_name ? ` by ${emp.released_by_name}` : ''}` : 'Released'}
                          >
                            <Chip label="Released" size="small" color="success" sx={{ fontWeight: 600 }} />
                          </Tooltip>
                        ) : (
                          <Chip label="Pending" size="small" sx={{ fontWeight: 600 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          disabled={emp.released || releasingId === emp.employee_id || !period}
                          onClick={() => period && release(emp.employee_id, period.year, period.month)}
                        >
                          {releasingId === emp.employee_id
                            ? 'Releasing…'
                            : emp.released
                              ? 'Released'
                              : 'Release Salary'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Counts are the number of distinct days with a punch-in / punch-out. Releasing records the
          month as released; generate the actual salary slip in Payroll.
        </Typography>
      </Container>
    </Layout>
  );
};

export default MonthlySalaryReview;
